/**
 * plugins/vuetify.ts
 *
 * Framework documentation: https://vuetifyjs.com`
 */

import { colors } from '@manager/config/color-config';
import {
  modalZIndex,
  overlayZIndex,
  snackbarZIndex,
} from '@manager/config/constants';
import { customIcons } from '@manager/plugins/custom-icons';
import { formErrorFeedbackInit } from '@manager/plugins/vuetify/form-error-feedback-plugin';
import { getPersistedLanguage } from '@manager/utils/user-settings-utils';
import type { App } from 'vue';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import { mdi } from 'vuetify/iconsets/mdi';
import { ca, cs, de, el, es, fi, fr, pl, pt, sl } from 'vuetify/locale';

export function getVuetifyInstance() {
  return createVuetify({
    components,
    locale: {
      locale: getPersistedLanguage(),
      fallback: 'en',
      messages: { de, es, fr, pl, pt, el, cs, sl, fi, ca },
    },
    date: {
      locale: {
        en: 'en-GB',
      },
    },
    theme: {
      defaultTheme: 'light',
      themes: {
        light: {
          colors: {
            primary: colors.app.primary,
            black: colors.black,
            error: colors.app.danger,
            background: '#ffffff',
            surface: '#ffffff',
            'desaturated-primary': colors.app['primary-light'],
            'kinn-white': colors.white,
            secondary: colors.blue['100'],
            'dark-grey': colors.grey['700'],
            info: colors.blue.DEFAULT,
          },
        },
      },
    },
    defaults: {
      VDialog: {
        zIndex: modalZIndex,
      },
      VBtn: {
        rounded: 'md',
      },
      VCard: {
        rounded: 'md',
      },
      VSnackbar: {
        zIndex: snackbarZIndex,
      },
      VNavigationDrawer: {
        zIndex: modalZIndex,
      },
      VTooltip: {
        zIndex: overlayZIndex,
      },
      VMenu: {
        zIndex: overlayZIndex,
      },
      VAutocomplete: {
        color: 'primary',
        variant: 'underlined',
        autoSelectFirst: true,
        menuProps: { zIndex: overlayZIndex },
      },
      VSelect: {
        color: 'primary',
        variant: 'underlined',
        menuProps: { zIndex: overlayZIndex },
      },
      VCombobox: {
        color: 'primary',
        variant: 'underlined',
        menuProps: { zIndex: overlayZIndex },
      },
      VTextField: {
        color: 'primary',
        variant: 'underlined',
      },
      VDateInput: {
        color: 'primary',
        variant: 'underlined',
        firstDayOfWeek: 1,
      },
      VDatePicker: {
        firstDayOfWeek: 1,
      },
      VProgressLinear: {
        color: 'primary',
      },
      VTextarea: {
        color: 'primary',
        variant: 'underlined',
        autoGrow: true,
        rows: 1,
      },
      VDataTable: {
        color: 'primary',
      },
      VCheckbox: {
        color: 'primary',
      },
      VChip: {
        color: 'primary',
        rounded: 'lg',
      },
      VRadioGroup: {
        color: 'primary',
      },
      VStepper: {
        color: 'primary',
      },
      VSwitch: {
        color: 'primary',
        inset: true,
      },
      VFileInput: {
        color: 'primary',
        variant: 'underlined',
      },
      VStepperItem: {
        color: 'primary',
      },
    },
    icons: {
      defaultSet: 'mdi',
      sets: {
        mdi,
        custom: customIcons,
      },
    },
  });
}

export const vuetify = getVuetifyInstance();

export function vuetifyInit(app: App) {
  app.use(vuetify);
  formErrorFeedbackInit(app);
}
